import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import type { LocalAudioTrack, RemoteParticipant, Room } from "twilio-video";

import { JoiningSound, RingingTone } from "assets/videocall";
import { AnalyticsEvents, AnalyticsService } from "utils/analytics";

import { AudioWarningBanner } from "../components/AudioWarningBanner";
import { CallButtons } from "../components/CallButtons";
import { CallingStateModal } from "../components/CallingStateModal";
import { MainParticipant } from "../components/mainParticipant/MainParticipant";
import { RemoteParticipant as RemoteParticipantView } from "../components/remoteParticipant/RemoteParticipant";
import { useAudioLevelMonitor } from "../hooks/useAudioLevelMonitor";
import { useDraggable } from "../hooks/useDraggable";
import { videoCall } from "../queries/videoCall";
import type { CallData, CallState, VideoCallMode } from "../types";

import {
  BottomGradient,
  DragBar,
  IdConfirmation,
  ResizeHandle,
  RoomContainer,
  StatusText,
  TopGradient,
} from "./RoomStyles";

const ringingTone = new Audio();
ringingTone.setAttribute("loop", "true");
ringingTone.volume = 0.25;

const joiningSound = new Audio(JoiningSound);

interface Props {
  callData: CallData;
  videoCallMode: VideoCallMode;
  setVideoCallMode: (mode: VideoCallMode) => void;
  onCallEnded: () => void;
}

export const VideoCall: React.FC<Props> = ({ callData, videoCallMode, setVideoCallMode, onCallEnded }) => {
  const { caller, callee } = callData;
  const { t } = useTranslation();
  const [room, setRoom] = useState<Room | null>(null);
  const [participant, setParticipant] = useState<RemoteParticipant | null>(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [callState, setCallState] = useState<CallState>("IDLE");
  const [isRoomVisible, setIsRoomVisible] = useState(true);
  const [callAgain, setCallAgain] = useState(false);
  const [localAudioTrack, setLocalAudioTrack] = useState<LocalAudioTrack | null>(null);
  const [remoteAudioTrack, setRemoteAudioTrack] = useState<MediaStreamTrack | null>(null);
  const [showLocalAudioWarning, setShowLocalAudioWarning] = useState(false);
  const [showRemoteAudioWarning, setShowRemoteAudioWarning] = useState(false);
  const [audioMonitorReset, setAudioMonitorReset] = useState(false);

  const { containerProps, resizeHandleProps, reset: resetDraggable } = useDraggable();
  const callStateRef = useRef<CallState>("IDLE");
  const callIdRef = useRef<number | null>(null);

  const minimised = videoCallMode === "minimised";
  const isFloating = videoCallMode === "floating";

  // Reset drag/resize state when leaving floating mode
  useEffect(() => {
    if (!isFloating) {
      resetDraggable();
    }
  }, [isFloating, resetDraggable]);

  // Monitor local audio level
  const localAudioLevel = useAudioLevelMonitor(localAudioTrack?.mediaStreamTrack, {
    enabled: audioEnabled,
    checkForIssuesAfter: 8000,
    onNoAudioDetected: () => {
      setShowLocalAudioWarning(true);
      AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.LOCAL.NO_AUDIO_WARNING_SHOWN);
    },
    resetKey: audioMonitorReset,
  });

  // Monitor remote audio level
  const remoteAudioLevel = useAudioLevelMonitor(remoteAudioTrack, {
    checkForIssuesAfter: 8000,
    onNoAudioDetected: () => {
      setShowRemoteAudioWarning(true);
      AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.REMOTE.NO_AUDIO_WARNING_SHOWN);
    },
  });

  // Auto-dismiss warnings when audio recovers
  useEffect(() => {
    if (showLocalAudioWarning && localAudioLevel > 0) {
      setShowLocalAudioWarning(false);
      AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.LOCAL.AUDIO_BACK_ON);
    }
  }, [localAudioLevel, showLocalAudioWarning]);

  useEffect(() => {
    if (showRemoteAudioWarning && remoteAudioLevel > 0) {
      setShowRemoteAudioWarning(false);
      AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.REMOTE.AUDIO_BACK_ON);
    }
  }, [remoteAudioLevel, showRemoteAudioWarning]);

  useEffect(() => {
    callStateRef.current = callState;

    if (callState === "CALLER_CONNECTED") {
      ringingTone.src = RingingTone;
      ringingTone.play();
    } else {
      ringingTone.pause();
    }

    return () => {
      ringingTone.src = "";
    };
  }, [callState]);

  useEffect(() => {
    const onParticipantConnected = (remoteParticipant: RemoteParticipant) => {
      AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.PARTICIPANT_JOINED, {
        callId: callIdRef.current,
        label: "patient",
        patient: callData.callee.id,
      });
      setParticipant(remoteParticipant);
      setCallState("CALLEE_CONNECTED");
      joiningSound.play();

      // Monitor remote audio when track is subscribed
      remoteParticipant.on("trackSubscribed", track => {
        if (track.kind === "audio") {
          const audioTrack = track as { mediaStreamTrack?: MediaStreamTrack };
          if (audioTrack.mediaStreamTrack) {
            setRemoteAudioTrack(audioTrack.mediaStreamTrack);
          }
        }
      });

      // Check if remote participant already has audio tracks
      remoteParticipant.audioTracks.forEach(publication => {
        if (publication.track) {
          const audioTrack = publication.track as { mediaStreamTrack?: MediaStreamTrack };
          if (audioTrack.mediaStreamTrack) {
            setRemoteAudioTrack(audioTrack.mediaStreamTrack);
          }
        }
      });
    };

    const onParticipantDisconnected = () => {
      AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.ENDED, {
        callId: callIdRef.current,
        label: "patient",
        patient: callData.callee.id,
      });
      setParticipant(null);
      setRemoteAudioTrack(null);
      setVideoCallMode("docked");
      setCallState("CALLEE_DISCONNECTED");
      setIsRoomVisible(false);
    };

    const onDisconnected = () => {
      if (callStateRef.current === "CALLER_CONNECTED") {
        setRoom(null);
        setVideoCallMode("docked");
        setCallState("CALLEE_REJECTED");
        setIsRoomVisible(false);
      }
    };

    const connectToRoom = async () => {
      try {
        setCallState("CALLER_CONNECTING");
        const Video = await import("twilio-video");
        const response = await videoCall(callData.caller.id, callData.callee.id);
        callIdRef.current = response.data.id;
        const newRoom = await Video.connect(response.data.token, {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false,
          },
          video: true,
        });
        newRoom.on("participantConnected", onParticipantConnected);
        newRoom.on("participantDisconnected", onParticipantDisconnected);
        newRoom.on("disconnected", onDisconnected);
        setRoom(newRoom);

        // Set local tracks for monitoring
        const localAudio = Array.from(newRoom.localParticipant.audioTracks.values())[0]?.track;
        if (!localAudio) {
          AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.LOCAL.NO_AUDIO_TRACK);
        }
        setLocalAudioTrack(localAudio || null);

        AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.PARTICIPANT_JOINED, {
          callId: callIdRef.current,
          label: "pro",
          patient: callData.callee.id,
        });
        setCallState("CALLER_CONNECTED");
      } catch (e) {
        setVideoCallMode("docked");
        setCallState("CONNECTION_FAILED");
        setIsRoomVisible(false);
      }
    };

    if (callData) {
      connectToRoom();
    }

    return () => {
      // Cleanup handled by hooks
      setLocalAudioTrack(null);
      setRemoteAudioTrack(null);
    };
  }, [callData, callAgain]);

  const callAgainClick = useCallback(() => {
    setCallAgain(prevState => !prevState);
    setIsRoomVisible(true);
  }, [callAgain]);

  const endCall = useCallback(() => {
    if (callState === "CALLER_CONNECTED") {
      ringingTone.pause();
    }

    if (room) {
      room.disconnect();
    }

    setCallState("IDLE");

    onCallEnded();
  }, [callState, onCallEnded, room]);

  const onHangup = useCallback(() => {
    AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.ENDED, {
      callId: callIdRef.current,
      label: "pro",
      patient: callData.callee.id,
    });
    endCall();
  }, [callData.callee.id, endCall]);

  const handleAudioInputChange = useCallback(
    async (deviceId: string) => {
      if (!localAudioTrack) return;

      try {
        // Use Twilio's built-in restart method to switch devices
        await localAudioTrack.restart({ deviceId: { exact: deviceId } });

        // Dismiss existing warning and reset audio monitoring to recheck new device
        setShowLocalAudioWarning(false);
        setAudioMonitorReset(prev => !prev);

        AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.AUDIO_DEVICE.INPUT_SWITCH_SUCCESS);
      } catch (error) {
        AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.AUDIO_DEVICE.INPUT_SWITCH_ERROR, {
          error: String(error),
        });
      }
    },
    [localAudioTrack]
  );

  const handleAudioOutputChange = useCallback(
    async (deviceId: string) => {
      if (!room) return;

      try {
        // Get all remote audio tracks and set their sink ID
        room.participants.forEach(remoteParticipant => {
          remoteParticipant.audioTracks.forEach(({ track }) => {
            if (track && track.kind === "audio") {
              const mediaElement = track.attach();
              if ("setSinkId" in mediaElement) {
                (mediaElement as HTMLMediaElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(deviceId);
              }
            }
          });
        });

        AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.AUDIO_DEVICE.OUTPUT_SWITCH_SUCCESS);
      } catch (error) {
        AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.AUDIO_DEVICE.OUTPUT_SWITCH_ERROR, {
          error: String(error),
        });
      }
    },
    [room]
  );

  const renderIdentityConfirmationText = () => {
    return !minimised && callState === "CALLEE_CONNECTED" ? (
      <IdConfirmation>
        <span>
          <b>{callData.callee.name}</b> - {t("videocall.identification_confirmation")}
        </span>
      </IdConfirmation>
    ) : null;
  };

  const renderStatusText = () => {
    if (!minimised) {
      if (callState === "CALLER_CONNECTING") {
        return <StatusText>{t("videocall.connecting")}</StatusText>;
      }
      if (callState === "CALLER_CONNECTED") {
        return <StatusText>{`${t("videocall.calling")} ${callee.name}...`}</StatusText>;
      }
    }

    return null;
  };

  const renderRoom = () => {
    return room !== null ? (
      <>
        {participant && (
          <RemoteParticipantView
            audioLevel={remoteAudioLevel}
            audioWarning={showRemoteAudioWarning}
            minimised={minimised}
            participant={participant}
            username={callee.name}
          />
        )}

        {(!minimised || participant === null) && (
          <MainParticipant
            audioEnabled={audioEnabled}
            audioLevel={localAudioLevel}
            audioWarning={showLocalAudioWarning}
            hasRemoteParticipantConnected={participant !== null}
            minimised={minimised}
            participant={room.localParticipant}
            username={caller.name}
            videoEnabled={videoEnabled}
          />
        )}

        {!minimised && (
          <>
            <TopGradient />
            <BottomGradient />
            {showLocalAudioWarning && (
              <AudioWarningBanner type="local" onClose={() => setShowLocalAudioWarning(false)} />
            )}
            {showRemoteAudioWarning && participant && (
              <AudioWarningBanner type="remote" onClose={() => setShowRemoteAudioWarning(false)} />
            )}
          </>
        )}
      </>
    ) : null;
  };

  const renderCallButtons = () => {
    return callState !== "IDLE" && callState !== "CALLER_CONNECTING" ? (
      <CallButtons
        audioEnabled={audioEnabled}
        minimised={minimised}
        videoCallMode={videoCallMode}
        videoEnabled={videoEnabled}
        onAudioEnabledToggled={() => setAudioEnabled(!audioEnabled)}
        onEndPressed={onHangup}
        onModeChange={setVideoCallMode}
        onVideoEnabledToggled={() => setVideoEnabled(!videoEnabled)}
        onAudioInputChange={handleAudioInputChange}
        onAudioOutputChange={handleAudioOutputChange}
      />
    ) : null;
  };

  return (
    <>
      {isRoomVisible ? (
        <RoomContainer
          data-testid="video-call-container"
          $minimised={minimised}
          $mode={videoCallMode}
          {...(isFloating ? containerProps : {})}
        >
          {isFloating && <DragBar />}

          {renderRoom()}

          {isFloating && <ResizeHandle {...resizeHandleProps} />}

          {renderIdentityConfirmationText()}

          {renderStatusText()}

          {renderCallButtons()}
        </RoomContainer>
      ) : (
        <CallingStateModal callState={callState} error={null} onCallAgain={callAgainClick} onEndCall={endCall} />
      )}
    </>
  );
};
